import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {ProgressBar} from "primereact/progressbar";
import {ProgressSpinner} from "primereact/progressspinner";
import {Toast} from "primereact/toast";
import Avatar from "../../system/components/avatar";
import Icon from "../../system/components/icon";
import ProgressCircle from "../../system/components/progress-circle";
import BoxHeader from "../components/box-header";
import BarChart from "../../system/components/bar-chart";
import LineChart from "../../system/components/line-chart";
import {dateToPersian} from "../../system/utils";
import {useTranslate} from "../../system/useTranslate";
import {useMember} from "../../models/member/use-member";
import {useTask} from "../../models/task/use-task";
import {useChat} from "../../models/chat/use-chat";
import {buildProfileMetrics, isSameEntity} from "./member-profile-metrics";

const entityId = (item) => item?.documentId || item?.id;
const relationMember = (item) => item?.member || item;

function formatNumber(value) {
    return new Intl.NumberFormat("fa-IR", {maximumFractionDigits: 1}).format(Number(value || 0));
}

function formatPercent(value) {
    return value === null || value === undefined ? "—" : `${formatNumber(value)}٪`;
}

function formatActivityDate(value) {
    if (!value) return "—";
    try {
        return dateToPersian(value, "YYYY/MM/DD - HH:mm");
    } catch (error) {
        return "—";
    }
}

function Index() {
    const params = useParams();
    const translate = useTranslate();
    const memberApi = useMember();
    const taskApi = useTask();
    const [memberData, setMemberData] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loadingMember, setLoadingMember] = useState(true);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [error, setError] = useState(null);
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const workspace = workspaceMembershipData?.workspace;
    const workspaceMemberships = useMemo(
        () => workspace?.workspace_memberships || [],
        [workspace?.workspace_memberships],
    );
    const workspaceKey = entityId(workspace);

    const targetMembership = useMemo(() => workspaceMemberships.find((membership) => (
        isSameEntity(membership, memberData)
    )), [workspaceMemberships, memberData]);

    useEffect(() => {
        let active = true;
        setLoadingMember(true);
        setError(null);
        setMemberData(null);
        setTasks([]);
        memberApi.getMember(params.id)
            .then((data) => {
                if (active) setMemberData(data || null);
            })
            .catch((requestError) => {
                if (active) setError(requestError);
            })
            .finally(() => {
                if (active) setLoadingMember(false);
            });
        return () => { active = false; };
        // API hooks intentionally expose new function identities per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id]);

    useEffect(() => {
        let active = true;
        if (!memberData || !workspaceKey) return () => { active = false; };
        if (!targetMembership) {
            setTasks([]);
            return () => { active = false; };
        }

        const membershipFilter = targetMembership.documentId
            ? {documentId: {$eq: targetMembership.documentId}}
            : {id: {$eq: targetMembership.id}};
        const workspaceFilter = workspace.documentId
            ? {documentId: {$eq: workspace.documentId}}
            : {id: {$eq: workspace.id}};
        setLoadingTasks(true);
        taskApi.findTasks({
            filters: {$and: [
                {$or: [{assignee: membershipFilter}, {members: membershipFilter}]},
                {workspace: workspaceFilter},
                {archivedAt: {$null: true}},
            ]},
            sort: ["updatedAt:desc", "createdAt:desc"],
            pagination: {pageSize: 500, withCount: false},
            populate: {
                workspace: {fields: ["id", "documentId"]},
                creatorUser: {populate: {member: {populate: ["profileImage"]}}},
                assignee: {populate: {member: {populate: ["profileImage"]}}},
                members: {populate: {member: {populate: ["profileImage"]}}},
                projects: {fields: ["id", "documentId", "title", "projectStatus"]},
                task_type: true,
            },
        }).then((items) => {
            if (active) setTasks(items || []);
        }).catch((requestError) => {
            if (active) setError(requestError);
        }).finally(() => {
            if (active) setLoadingTasks(false);
        });
        return () => { active = false; };
        // API hooks intentionally expose new function identities per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberData?.documentId, memberData?.id, targetMembership?.documentId, targetMembership?.id, workspaceKey]);

    const profile = useMemo(() => buildProfileMetrics({
        member: memberData,
        membership: targetMembership,
        tasks,
        projects: workspace?.projects || [],
        goals: workspace?.goals || [],
        teams: workspace?.teams || [],
        workspaceMemberships,
    }), [memberData, targetMembership, tasks, workspace?.projects, workspace?.goals, workspace?.teams, workspaceMemberships]);

    if (loadingMember) {
        return <div className="h-full flex align-items-center justify-content-center surface-50"><ProgressSpinner style={{width: "3rem", height: "3rem"}} strokeWidth="4"/></div>;
    }

    if (!memberData || error) {
        return <div className="h-full p-6 surface-50"><Message className="w-full" severity="error" text={translate("profile.load-error")}/></div>;
    }

    return (
        <div className="flex flex-column overflow-auto h-full surface-50 pb-5">
            <MemberIdentification memberData={memberData} membership={targetMembership} profile={profile}/>
            <QuickDetails profile={profile}/>
            <JobDiscipline profile={profile} loading={loadingTasks}/>
            <ActivitiesAnalysis profile={profile} loading={loadingTasks}/>
            <div className="grid m-0 px-3">
                <div className="col-12 lg:col-9 p-0">
                    <SuccessRate profile={profile}/>
                    <Projects profile={profile}/>
                    <Goals profile={profile}/>
                    <Teams profile={profile}/>
                    <Activities profile={profile} loading={loadingTasks}/>
                </div>
                <aside className="col-12 lg:col-3 px-3">
                    <WorkingTemplate profile={profile} loading={loadingTasks}/>
                    <CoWorkers profile={profile}/>
                </aside>
            </div>
        </div>
    );
}

function MemberIdentification({memberData, membership, profile}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    const chatApi = useChat();
    const toast = useRef(null);
    const [resumeVisible, setResumeVisible] = useState(false);
    const [openingChat, setOpeningChat] = useState(false);
    const currentUser = useSelector((state) => state.auth.user);

    const openConversation = async () => {
        if (openingChat) return;
        if (isSameEntity(memberData, currentUser)) {
            navigate("/inbox?tab=conversations");
            return;
        }
        if (!membership?.documentId) {
            toast.current?.show({severity: "error", summary: translate("profile.action-error"), detail: translate("profile.membership-unavailable"), life: 4000});
            return;
        }
        setOpeningChat(true);
        try {
            const chat = await chatApi.createChat({type: "private", users: {connect: [{documentId: membership.documentId}]}});
            if (!entityId(chat)) throw new Error("profile-chat-create-failed");
            navigate(`/inbox?tab=conversations&chat=${entityId(chat)}`);
        } catch (requestError) {
            toast.current?.show({severity: "error", summary: translate("profile.action-error"), detail: translate("profile.message-open-failed"), life: 4000});
        } finally {
            setOpeningChat(false);
        }
    };

    const address = [memberData?.address?.city, memberData?.address?.country].filter(Boolean).join("، ");
    return (
        <>
            <Toast ref={toast} position="bottom-left"/>
            <section className="flex flex-column p-4 md:p-6">
                <div className="flex flex-column md:flex-row align-items-center md:align-items-start gap-3">
                    <Avatar size="xlarge" className="text-4xl border-round-2xl font-bold border-3 border-white shadow-2 w-8rem h-8rem" shape="square" borderColorRange="transparent" memberData={memberData} fallbackPreset={profile.fallbackPreset} unStyled/>
                    <div className="flex flex-column gap-2 p-2 align-items-center md:align-items-start text-center md:text-right">
                        <div className="flex flex-wrap align-items-center justify-content-center md:justify-content-start gap-2">
                            <h1 className="font-bold text-3xl md:text-4xl m-0">{memberData?.fullname || translate("profile.unknown-member")}</h1>
                            {memberData?.jobPosition && <span className="bg-primary-50 border-1 border-round border-primary-100 px-2 py-1 text-xs text-primary">{memberData.jobPosition}</span>}
                        </div>
                        {memberData?.about && <span className="text-500 text-sm line-height-3">{memberData.about}</span>}
                        <div className="flex flex-wrap align-items-center justify-content-center md:justify-content-start gap-3">
                            {address && <IconLabel icon="mi mi:location_on" label={address}/>}
                            <IconLabel icon="mi mi:verified" iconClassName={memberData?.verified ? "text-green-500" : "text-yellow-400"} label={translate(memberData?.verified ? "verified-profile" : "not-verified-profile")}/>
                        </div>
                        <div className="flex align-items-center gap-2 pt-2">
                            <Button type="button" className="p-button-sm text-xs" icon="pi pi-comments" loading={openingChat} label={translate("send-message")} onClick={openConversation}/>
                            <Button type="button" className="p-button-sm text-xs p-button-secondary p-button-outlined" icon="pi pi-id-card" label={translate("resume")} onClick={() => setResumeVisible(true)}/>
                        </div>
                    </div>
                </div>
            </section>
            <ResumeDialog visible={resumeVisible} onHide={() => setResumeVisible(false)} memberData={memberData} profile={profile}/>
        </>
    );
}

function ResumeDialog({visible, onHide, memberData, profile}) {
    const translate = useTranslate();
    const stats = profile.stats;
    return (
        <Dialog
            visible={visible}
            modal
            dismissableMask
            className="rtl w-11 md:w-8 lg:w-6"
            header={translate("profile.resume-title")}
            onHide={onHide}
            footer={<Button type="button" outlined className="text-xs" label={translate("close")} onClick={onHide}/>}
        >
            <div className="flex flex-column gap-4">
                <div className="flex align-items-center gap-3">
                    <Avatar size="xlarge" memberData={memberData} fallbackPreset={profile.fallbackPreset}/>
                    <div className="flex flex-column gap-1"><strong className="text-xl">{memberData?.fullname}</strong><span className="text-500">{memberData?.jobPosition || translate("profile.job-not-set")}</span></div>
                </div>
                {memberData?.about && <p className="m-0 line-height-3">{memberData.about}</p>}
                <div className="grid m-0 surface-50 border-round-xl">
                    <ResumeMetric label={translate("all-projects")} value={stats.projects}/>
                    <ResumeMetric label={translate("tasks")} value={stats.tasks}/>
                    <ResumeMetric label={translate("done-tasks")} value={stats.completedTasks}/>
                    <ResumeMetric label={translate("success-rate")} value={formatPercent(stats.overallSuccessRate)} raw/>
                </div>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <strong>{translate("profile.recent-projects")}</strong>
                        <ul className="line-height-3 pr-4">{profile.relatedProjects.slice(0, 5).map((project) => <li key={entityId(project)}>{project.title}</li>)}{!profile.relatedProjects.length && <li className="text-500">{translate("datatable-empty-message")}</li>}</ul>
                    </div>
                    <div className="col-12 md:col-6">
                        <strong>{translate("profile.recent-tasks")}</strong>
                        <ul className="line-height-3 pr-4">{profile.latestActivities.slice(0, 5).map((task) => <li key={entityId(task)}>{task.title}</li>)}{!profile.latestActivities.length && <li className="text-500">{translate("datatable-empty-message")}</li>}</ul>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

function ResumeMetric({label, value, raw = false}) {
    return <div className="col-6 md:col-3 flex flex-column align-items-center gap-1 p-3"><span className="text-500 text-xs">{label}</span><strong className="text-xl">{raw ? value : formatNumber(value)}</strong></div>;
}

function QuickDetails({profile}) {
    const translate = useTranslate();
    const values = [
        ["all-projects", profile.stats.projects], ["active-goals", profile.stats.activeGoals],
        ["done-tasks", profile.stats.completedTasks], ["tasks", profile.stats.tasks],
        ["teams", profile.stats.teams], ["success", formatPercent(profile.stats.overallSuccessRate), true],
        ["overDues", profile.stats.delays], ["coop", profile.stats.collaborations],
    ];
    return <section className="px-4 md:px-6 py-3"><CardView className="flex flex-wrap bg-white overflow-hidden">{values.map(([label, value, raw]) => <div key={label} className="flex flex-column align-items-center justify-content-center gap-2 p-3 border-200 border-bottom-1 md:border-left-1" style={{flex: "1 1 8rem"}}><span className="text-600">{translate(label)}</span><strong className="text-3xl">{raw ? value : formatNumber(value)}</strong></div>)}</CardView></section>;
}

function JobDiscipline({profile, loading}) {
    const translate = useTranslate();
    const dayKeys = ["saturday", "sunday", "monday", "tuesday", "wednesday", "thursday", "friday"];
    const chartData = {activity: {labels: dayKeys.map(translate), datasets: [{data: profile.weekdayActivity, label: "profile.activity-events", backgroundColor: "#f59e0b"}]}};
    return <Section title={translate("job-discipline")}><CardView className="grid m-0 align-items-center bg-white p-3">
        <div className="col-12 md:col-3 flex flex-column align-items-center justify-content-center gap-2"><MetricCircle value={profile.stats.onTimeRate}/><span>{translate("deadlines-control")}</span>{profile.stats.onTimeRate === null && <small className="text-500">{translate("profile.no-deadline-data")}</small>}</div>
        <div className="col-12 md:col-6 flex flex-column align-items-center justify-content-center" style={{minHeight: "13rem"}}><BarChart className="w-full h-12rem" chartData={chartData} loading={loading}/><span>{translate("keep-working")}</span></div>
        <div className="col-12 md:col-3 flex flex-column gap-2 align-items-center justify-content-center"><div className="flex align-items-baseline gap-1"><strong className="text-5xl">{formatNumber(profile.stats.averageDelayDays)}</strong><span className="text-500">{translate("day")}</span></div><span>{translate("over-time-avg")}</span></div>
    </CardView></Section>;
}

function ActivitiesAnalysis({profile, loading}) {
    const translate = useTranslate();
    const chartData = {activity: {labels: profile.monthBuckets.map((bucket) => bucket.label), datasets: [
        {data: profile.monthBuckets.map((bucket) => bucket.assigned), label: "profile.assigned-tasks", backgroundColor: "#f59e0b", borderColor: "#f59e0b", tension: 0.35},
        {data: profile.monthBuckets.map((bucket) => bucket.completed), label: "completed-tasks", backgroundColor: "#ef5b0c", borderColor: "#ef5b0c", tension: 0.35},
    ]}};
    return <Section title={translate("activities-analysis")}><CardView className="bg-white p-3" style={{minHeight: "14rem"}}><LineChart className="w-full h-13rem" chartData={chartData} loading={loading}/></CardView></Section>;
}

function SuccessRate({profile}) {
    const translate = useTranslate();
    const bars = [["managed-projects", profile.stats.managedProjectSuccessRate], ["goals-achieved", profile.stats.goalSuccessRate], ["collaborating-in-projects", profile.stats.collaborationSuccessRate]];
    return <Section title={translate("success-rate")}><CardView className="grid m-0 align-items-center bg-white p-3">
        <div className="col-12 md:col-4 flex flex-column align-items-center gap-2"><MetricCircle value={profile.stats.overallSuccessRate}/><span>{translate("profile.overall-success")}</span></div>
        <div className="col-12 md:col-8 flex flex-column gap-4">{bars.map(([label, value]) => <ProgressMetric key={label} label={translate(label)} value={value}/>)}</div>
    </CardView></Section>;
}

function MetricCircle({value}) {
    return <div className="relative w-8rem h-8rem flex align-items-center justify-content-center"><ProgressCircle value={value || 0} color="var(--primary-500)" displayText={false} strokeWidth="0.35rem" radius={36}/><strong className="absolute text-2xl">{formatPercent(value)}</strong></div>;
}

function ProgressMetric({label, value}) {
    return <div className="flex flex-column w-full gap-1"><div className="flex align-items-center justify-content-between text-500 text-xs"><span>{label}</span><span>{formatPercent(value)}</span></div><ProgressBar showValue={false} style={{height: "10px"}} className="w-full" value={value || 0}/></div>;
}

function Projects({profile}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    return <DataSection title={translate("projects")} value={profile.relatedProjects} onRowClick={(row) => navigate(`/projects/${entityId(row)}`)}>
        <Column field="title" header={translate("profile.project-title")}/><Column header={translate("profile.manager")} body={(row) => relationMember(row?.owner)?.fullname || translate("profile.no-manager")}/><Column header={translate("status")} body={(row) => translate(row.projectStatus || "no-status")}/><Column header={translate("profile.progress")} body={(row) => row.progress === undefined || row.progress === null ? "—" : formatPercent(row.progress)}/>
    </DataSection>;
}

function Goals({profile}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    return <DataSection title={translate("goals")} value={profile.relatedGoals} onRowClick={(row) => navigate(`/goals/${entityId(row)}`)}>
        <Column field="title" header={translate("profile.goal-title")}/><Column header={translate("profile.owner")} body={(row) => relationMember(row?.owner)?.fullname || "—"}/><Column header={translate("status")} body={(row) => translate(row.goalStatus || "no-status")}/><Column header={translate("profile.due-date")} body={(row) => row.dueDate ? dateToPersian(row.dueDate, "YYYY/MM/DD") : "—"}/>
    </DataSection>;
}

function Teams({profile}) {
    const translate = useTranslate();
    const navigate = useNavigate();
    return <DataSection title={translate("teams")} value={profile.relatedTeams} onRowClick={(row) => navigate(`/teams/${entityId(row)}`)}>
        <Column header={translate("profile.team-title")} body={(row) => row.name || row.title || "—"}/><Column header={translate("profile.manager")} body={(row) => relationMember(row?.owner)?.fullname || translate("profile.no-manager")}/><Column header={translate("profile.members-count")} body={(row) => formatNumber((row.members || []).length)}/>
    </DataSection>;
}

function Activities({profile, loading}) {
    const translate = useTranslate();
    return <DataSection title={translate("latest-activities")} value={profile.latestActivities} loading={loading}>
        <Column field="title" header={translate("profile.activity-title")}/><Column header={translate("status")} body={(row) => translate(row.taskStatus || "no-status")}/><Column header={translate("profile.project-title")} body={(row) => row.projects?.map((project) => project.title).filter(Boolean).join("، ") || "—"}/><Column header={translate("profile.last-update")} body={(row) => formatActivityDate(row.activityAt)}/>
    </DataSection>;
}

function DataSection({title, value, loading = false, onRowClick, children}) {
    const translate = useTranslate();
    return <Section title={title}><CardView className="bg-white p-2 overflow-hidden"><DataTable value={value || []} loading={loading} dataKey="documentId" size="small" responsiveLayout="scroll" paginator={(value || []).length > 8} rows={8} emptyMessage={translate("datatable-empty-message")} selectionMode={onRowClick ? "single" : undefined} rowClassName={onRowClick ? "cursor-pointer" : undefined} onRowClick={(event) => onRowClick?.(event.data)}>{children}</DataTable></CardView></Section>;
}

function CoWorkers({profile}) {
    const translate = useTranslate();
    return <div className="py-3 flex flex-column gap-2"><BoxHeader title={translate("co-workers")}/><CardView className="flex flex-column bg-white p-3 gap-2">
        {profile.coworkers.map((coworker) => {
            const member = relationMember(coworker.membership);
            const sharedWork = translate("profile.shared-work")
                .replace("{{PROJECTS}}", formatNumber(coworker.sharedProjects))
                .replace("{{TEAMS}}", formatNumber(coworker.sharedTeams));
            return <div key={entityId(coworker.membership) || entityId(member)} className="flex align-items-center gap-2 border-bottom-1 border-100 pb-2"><Avatar memberData={member}/><div className="flex flex-column min-w-0"><strong className="white-space-nowrap overflow-hidden text-overflow-ellipsis">{member?.fullname}</strong><small className="text-500">{sharedWork}</small></div></div>;
        })}
        {!profile.coworkers.length && <span className="text-500 text-center py-3">{translate("datatable-empty-message")}</span>}
    </CardView></div>;
}

function WorkingTemplate({profile, loading}) {
    const translate = useTranslate();
    const dayKeys = ["saturday", "sunday", "monday", "tuesday", "wednesday", "thursday", "friday"];
    const chartData = {activity: {labels: dayKeys.map(translate), datasets: [{data: profile.weekdayActivity, label: "profile.activity-events", backgroundColor: "#ef5b0c"}]}};
    return <div className="py-3 flex flex-column gap-2 w-full"><BoxHeader title={translate("working-template")}/><CardView className="bg-white p-3" style={{minHeight: "15rem"}}><BarChart className="w-full h-13rem" chartData={chartData} loading={loading}/></CardView></div>;
}

function Section({title, children}) {
    return <section className="px-3 md:px-6 py-3 flex flex-column gap-2"><BoxHeader title={title}/>{children}</section>;
}

function CardView({children, className = "", onClick, style}) {
    return <div onClick={onClick} style={style} className={`${className} h-full text-xs w-full shadow-1 border-round-xl ${onClick ? "cursor-pointer hover:surface-50" : ""}`}>{children}</div>;
}

function IconLabel({icon, label, iconClassName = "text-lg text-400", labelClassName = "text-xs text-500"}) {
    return <div className="flex align-items-center gap-1"><Icon icon={icon} className={iconClassName}/><span className={labelClassName}>{label}</span></div>;
}

export default Index;
